import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';

type UserRole = 'writer' | 'reader';

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'reader' as UserRole
  });
  const [errors, setErrors] = useState<{
    username?: string;
    email?: string;
    password?: string;
    confirmPassword?: string;
  }>({});
  const [authError, setAuthError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors: {
      username?: string;
      email?: string;
      password?: string;
      confirmPassword?: string;
    } = {};

    if (!formData.username) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      // 1. Sign up with Supabase Auth
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            username: formData.username,
            role: formData.role,
          },
        },
      });

      if (signUpError) throw signUpError;

      const userId = data.user?.id;
      if (!userId) throw new Error('Account creation failed. Please try again.');

      // Profile is created automatically via database trigger.
      // If session exists, redirect immediately; otherwise prompt email confirmation.
      if (data.session) {
        navigate(formData.role === 'writer' ? '/profile/writer' : '/profile/reader');
      } else {
        setAuthError('Account created! Please check your email to confirm your account, then sign in.');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      setAuthError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleRoleChange = (role: UserRole) => {
    setFormData(prev => ({ ...prev, role }));
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center justify-center space-x-3 group">
            <img
              src="https://static.readdy.ai/image/cd80e8551adaa83aa4e45d1e4f56c590/c334bfc7bb42624cba9a352752f4949a.png"
              alt="Sinister Files Logo"
              className="h-16 w-16 object-contain transition-transform duration-300 group-hover:scale-110"
            />
            <span className="text-3xl font-bold text-red-600 font-['Cinzel'] tracking-wider">
              SINISTER FILES
            </span>
          </Link>
        </div>

        {/* Register Card */}
        <div className="bg-gradient-to-br from-[#1a0a0a] to-[#0f0f0f] border border-red-900/20 rounded-lg p-8 shadow-2xl shadow-red-900/20">
          <h1 className="text-3xl font-bold text-white mb-2 font-['Cinzel'] text-center">
            Join the Darkness
          </h1>
          <p className="text-gray-400 text-center mb-8 font-['Crimson_Text']">
            Create your account to begin
          </p>

          {/* Auth Error Banner */}
          {authError && (
            <div className="mb-6 px-4 py-3 bg-red-900/30 border border-red-600/50 rounded-lg flex items-start gap-2">
              <i className="ri-error-warning-line text-red-400 mt-0.5 shrink-0"></i>
              <p className="text-sm text-red-300 font-['Crimson_Text']">{authError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Role Selector */}
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-3 font-['Crimson_Text']">
                I want to join as
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => handleRoleChange('reader')}
                  className={`px-4 py-3 rounded-lg border-2 transition-all duration-300 font-['Crimson_Text'] font-semibold whitespace-nowrap cursor-pointer ${
                    formData.role === 'reader'
                      ? 'bg-red-600 border-red-600 text-white shadow-lg shadow-red-900/30'
                      : 'bg-[#0a0a0a] border-red-900/30 text-gray-400 hover:border-red-600/50'
                  }`}
                >
                  <i className="ri-book-open-line mr-2"></i>
                  Reader
                </button>
                <button
                  type="button"
                  onClick={() => handleRoleChange('writer')}
                  className={`px-4 py-3 rounded-lg border-2 transition-all duration-300 font-['Crimson_Text'] font-semibold whitespace-nowrap cursor-pointer ${
                    formData.role === 'writer'
                      ? 'bg-red-600 border-red-600 text-white shadow-lg shadow-red-900/30'
                      : 'bg-[#0a0a0a] border-red-900/30 text-gray-400 hover:border-red-600/50'
                  }`}
                >
                  <i className="ri-quill-pen-line mr-2"></i>
                  Writer
                </button>
              </div>
            </div>

            {/* Username Field */}
            <div>
              <label htmlFor="username" className="block text-sm font-semibold text-gray-300 mb-2 font-['Crimson_Text']">
                Username
              </label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                className={`w-full px-4 py-3 bg-[#0a0a0a] border ${
                  errors.username ? 'border-red-500' : 'border-red-900/30'
                } rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-600 transition-colors font-['Crimson_Text'] text-sm`}
                placeholder="Choose a username"
              />
              {errors.username && (
                <p className="mt-2 text-sm text-red-500 font-['Crimson_Text'] flex items-center">
                  <i className="ri-error-warning-line mr-1"></i>
                  {errors.username}
                </p>
              )}
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-300 mb-2 font-['Crimson_Text']">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-4 py-3 bg-[#0a0a0a] border ${
                  errors.email ? 'border-red-500' : 'border-red-900/30'
                } rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-600 transition-colors font-['Crimson_Text'] text-sm`}
                placeholder="your@email.com"
              />
              {errors.email && (
                <p className="mt-2 text-sm text-red-500 font-['Crimson_Text'] flex items-center">
                  <i className="ri-error-warning-line mr-1"></i>
                  {errors.email}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-gray-300 mb-2 font-['Crimson_Text']">
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`w-full px-4 py-3 bg-[#0a0a0a] border ${
                  errors.password ? 'border-red-500' : 'border-red-900/30'
                } rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-600 transition-colors font-['Crimson_Text'] text-sm`}
                placeholder="Create a password"
              />
              {errors.password && (
                <p className="mt-2 text-sm text-red-500 font-['Crimson_Text'] flex items-center">
                  <i className="ri-error-warning-line mr-1"></i>
                  {errors.password}
                </p>
              )}
            </div>

            {/* Confirm Password Field */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-300 mb-2 font-['Crimson_Text']">
                Confirm Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className={`w-full px-4 py-3 bg-[#0a0a0a] border ${
                  errors.confirmPassword ? 'border-red-500' : 'border-red-900/30'
                } rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-600 transition-colors font-['Crimson_Text'] text-sm`}
                placeholder="Confirm your password"
              />
              {errors.confirmPassword && (
                <p className="mt-2 text-sm text-red-500 font-['Crimson_Text'] flex items-center">
                  <i className="ri-error-warning-line mr-1"></i>
                  {errors.confirmPassword}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-6 py-3 bg-red-600 hover:bg-red-700 disabled:bg-red-800 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-all duration-300 shadow-lg shadow-red-900/30 hover:shadow-red-900/50 font-['Crimson_Text'] whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  Creating Account...
                </span>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          {/* Login Link */}
          <div className="mt-8 text-center">
            <p className="text-gray-400 font-['Crimson_Text']">
              Already have an account?{' '}
              <Link
                to="/login"
                className="text-red-500 hover:text-red-400 font-semibold transition-colors cursor-pointer"
              >
                Sign In
              </Link>
            </p>
          </div>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-6">
          <Link
            to="/"
            className="text-gray-400 hover:text-red-500 transition-colors font-['Crimson_Text'] text-sm cursor-pointer inline-flex items-center"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Back to Home
          </Link>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default Register;